import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Helpers from "../Helper/Helpers";
import Swal from "sweetalert2";
import toast from "react-hot-toast";

const ProductList = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await Helpers("/admin/product/get", "GET");
      if (res && res.status) {
        setProducts(res.data);
      }
    } catch (error) {
      console.error("Failed to fetch products:", error);
    }
  };

  const handleToggleBestSeller = async (id) => {
    try {
      const res = await Helpers(
        `/admin/product/toggle-bestselling/${id}`,
        "PUT"
      );
      if (res && res.status) {
        toast.success("Best Seller status updated");
        fetchProducts();
      } else {
        toast.error("Failed to update Best Seller status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error toggling Best Seller");
    }
  };

  const handleToggleArrival = async (id) => {
    try {
      const res = await Helpers(`/admin/product/toggle-arrival/${id}`, "PUT");
      if (res && res.status) {
        toast.success("Arrival status updated");
        fetchProducts();
      } else {
        toast.error("Failed to update Arrival status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error toggling Arrival");
    }
  };

  const handleDeleteProduct = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      const res = await Helpers(`/admin/product/delete/${id}`, "DELETE", {});
      if (res && res?.status) {
        toast.success("Deleted Successfully");
        fetchProducts();
      } else {
        toast?.error("Deletion failed");
      }
    }
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredProducts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  return (
    <div className="p-6 mt-14 w-full">
      {/* Header and Add Button */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Product List</h1>
        <button
          onClick={() => navigate("/admin/add-product")}
          className="bg-blue-600 text-white px-4 py-2 rounded"
        >
          Add Product
        </button>
      </div>

      {/* Search Field */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by product name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full md:w-1/3 border border-gray-300 px-3 py-2 rounded focus:outline-none"
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto w-full">
        <table className="w-full border border-gray-300">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 border">Id</th>
              <th className="p-3 border">Image</th>
              <th className="p-3 border">Name</th>
              <th className="p-3 border">Category</th>
              <th className="p-3 border">Sub Category</th>
              <th className="p-3 border">Price</th>
              <th className="p-3 border">Best Seller</th>
              <th className="p-3 border">New Arrival</th>
              <th className="p-3 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((product, index) => (
                <tr key={product._id} className="text-center">
                  <td className="p-2 border">{indexOfFirstItem + index + 1}</td>
                  <td className="p-2 border">
                    <img
                      src={product?.images?.[0]}
                      alt={product.name}
                      className="w-12 h-12 object-cover mx-auto rounded"
                    />
                  </td>
                  <td className="p-2 border">{product.name}</td>
                  <td className="p-2 border">{product.category?.name}</td>
                  <td className="p-2 border">{product.subcategory?.name}</td>
                  <td className="p-2 border">₹{product.price}</td>
                  <td className="p-2 border">
                    <button
                      onClick={() => handleToggleBestSeller(product._id)}
                      className={`px-2 py-1 text-sm rounded ${
                        product.bestselling
                          ? "bg-green-500 text-white"
                          : "bg-gray-300 text-black"
                      }`}
                    >
                      {product.bestselling ? "Yes" : "No"}
                    </button>
                  </td>
                  <td className="p-2 border">
                    <button
                      onClick={() => handleToggleArrival(product._id)}
                      className={`px-2 py-1 text-sm rounded ${
                        product.arrival
                          ? "bg-green-500 text-white"
                          : "bg-gray-300 text-black"
                      }`}
                    >
                      {product.arrival ? "Yes" : "No"}
                    </button>
                  </td>
                  <td className="p-2 border">
                    <button
                      onClick={() =>
                        navigate("/admin/update-product", { state: product })
                      }
                      className="text-blue-600 hover:underline mr-2"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product?._id)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="p-4 text-center text-gray-500">
                  No products found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4 items-center gap-2 flex-wrap">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Prev
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-3 py-1 border rounded ${
                currentPage === i + 1 ? "bg-blue-600 text-white" : ""
              }`}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductList;
